import BigNumber from 'bignumber.js'
import { PERCENT } from 'constants/number'
import { DoubleSlopeModelInfo } from 'interfaces/contracts/doubleSlope'
import { TripleSlopeModelInfo } from 'interfaces/contracts/tripleSlope'

export type IRMKind = 'FixRate' | 'TripleSlope' | 'DoubleSlope'
export type IRMCalKind = 'calFixRate' | 'calTripleSlope' | 'calDoubleSlope'

export type IRMData = {
  supplyAPR: number
  borrowAPR: number
  utilization: number
}

export const convertMemoToKind: { [k: string]: IRMKind } = {
  fix_rate: 'FixRate',
  double_slope: 'DoubleSlope',
  triple_slope: 'TripleSlope',
}

export const convertMemoToCalKind: { [k: string]: IRMCalKind } = {
  fix_rate: 'calFixRate',
  double_slope: 'calDoubleSlope',
  triple_slope: 'calTripleSlope',
}

class SimulateIRM {
  calFixRate = (
    adminFee: number,
    utilization: BigNumber,
    irm_info: string,
  ): IRMData => {
    const _rate = new BigNumber(irm_info)

    const oneMinusFee = 1 - adminFee
    const supplyAPR = _rate.times(utilization).times(oneMinusFee)
    return {
      supplyAPR: supplyAPR.times(PERCENT).toNumber(),
      borrowAPR: _rate.times(PERCENT).toNumber(),
      utilization: utilization.times(PERCENT).toNumber(),
    }
  }

  /**
   *
   * simulate graph for fix-rate IRM
   * @param adminFee number : 0 - 1
   * @param adminFee FixRateInstantiateMsg
   * @returns IRMData[]
   */
  FixRate = (adminFee: number = 0, irm_info: string): IRMData[] => {
    return Array.from(Array(101).keys()).map((_utilization) => {
      let utilization = new BigNumber(_utilization).div(PERCENT)

      return this.calFixRate(adminFee, utilization, irm_info)
    })
  }

  calTripleSlope = (
    adminFee: number,
    utilization: BigNumber,
    irm_info: TripleSlopeModelInfo,
  ): IRMData => {
    const _kink1 = new BigNumber(irm_info.kink1)
    const _kink2 = new BigNumber(irm_info.kink2)
    const _multiplier = new BigNumber(irm_info.multiplier)
    const _jump_multiplier = new BigNumber(irm_info.jump_multiplier)
    const _base_rate = new BigNumber(irm_info.base_rate)

    let borrowRate = new BigNumber('0')
    if (utilization.lte(_kink1)) {
      borrowRate = utilization.times(_multiplier).plus(_base_rate)
    } else if (utilization.lte(_kink2)) {
      borrowRate = _kink1.times(_multiplier).plus(_base_rate)
    } else {
      let normal_rate = _kink1.times(_multiplier).plus(_base_rate)
      borrowRate = utilization
        .minus(_kink2)
        .times(_jump_multiplier)
        .plus(normal_rate)
    }

    let supplyRate = utilization
      .times(borrowRate)
      .times(new BigNumber('1').minus(adminFee))

    return {
      supplyAPR: supplyRate.times(PERCENT).toNumber(),
      borrowAPR: borrowRate.times(PERCENT).toNumber(),
      utilization: utilization.times(PERCENT).toNumber(),
    }
  }

  /**
   * simulate graph for triple-slope IRM
   * @param adminFee number : 0 - 1
   * @param irm_info TripleSlopeModelInfo
   * @returns IRMData[]
   */
  TripleSlope = (
    adminFee: number = 0,
    irm_info: TripleSlopeModelInfo,
  ): IRMData[] => {
    return Array.from(Array(101).keys()).map((_utilization) => {
      let utilization = new BigNumber(_utilization).div(PERCENT)
      return this.calTripleSlope(adminFee, utilization, irm_info)
    })
  }

  calDoubleSlope = (
    adminFee: number,
    utilization: BigNumber,
    irm_info: DoubleSlopeModelInfo,
  ): IRMData => {
    const _base_rate = new BigNumber(irm_info.base_rate)
    const _multiplier = new BigNumber(irm_info.multiplier)
    const _jump_multiplier = new BigNumber(irm_info.jump_multiplier)
    const _kink1 = new BigNumber(irm_info.kink1)

    const oneMinusFee = 1 - adminFee

    let borrowAPR = utilization.times(_multiplier).plus(_base_rate)
    if (utilization.gt(_kink1)) {
      borrowAPR = borrowAPR.plus(
        utilization.minus(_kink1).times(_jump_multiplier),
      )
    }
    return {
      supplyAPR: utilization
        .times(PERCENT)
        .times(borrowAPR)
        .times(oneMinusFee)
        .toNumber(),
      borrowAPR: borrowAPR.times(PERCENT).toNumber(),
      utilization: utilization.times(PERCENT).toNumber(),
    }
  }

  /**
   * simulate graph for double-slope IRM
   * @param adminFee number : 0 - 1
   * @param irm_info DoubleSlopeModelInfo
   * @returns IRMData[]
   */
  DoubleSlope = (
    adminFee: number = 0,
    irm_info: DoubleSlopeModelInfo,
  ): IRMData[] => {
    return Array.from(Array(101).keys()).map((_utilization) => {
      const utilization = new BigNumber(_utilization).div(PERCENT)

      return this.calDoubleSlope(adminFee, utilization, irm_info)
    })
  }
}

export default new SimulateIRM()
